import React, { useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import {
  PageTitle,
  RequiredFieldIndicator,
  StepIndicator,
  Alert,
} from '@gsa/afp-component-library';
import { useAppAbility } from '@gsa/afp-shared-ui-utils';
import OverlaySpinner from '../../../components/overlay-spinner';
import { OPERATIONS, SUBJECTS } from '../../../utilities/constants';
import Breadcrumbs from '../../../widgets/breadcrumbs';
import {
  API_TIME_LIMIT,
  REPORT_CONFIG,
  STEPS,
} from './components/contract-report-types';
import ReportSelectionForm from './report-selection-form';
import SolicitationSelectionForm from './solicitation-selection-form';
import {
  getDownloadAlert,
  asyncDownloadAlert,
} from './components/contract-report-alerts';
import { GET_CONTRACT_JS_REPORT } from './components/contract-report-query';
import './components/contract-report.scss';
import {
  getJSReportQueryParams,
  REPORT_DEFAULTS,
  STEP_1_DEFAULTS,
  STEP_2_DEFAULTS,
} from './components/contract-report-helper';

let reportStartTime;

const ContractReports = () => {
  const canViewAll = useAppAbility().can(
    OPERATIONS.Manage,
    SUBJECTS.ContractReport,
  );

  const [currentStep, setCurrentStep] = useState(0);
  const [formValues, setFormValues] = useState({
    ...REPORT_DEFAULTS,
    ...STEP_1_DEFAULTS,
    ...STEP_2_DEFAULTS,
  });

  const [toaster, setToaster] = useState(null);

  const breadcrumbsPath = [
    {
      location: `${window.AFP_CONFIG.appURLs.home}/home`,
      label: 'Home',
    },
  ];

  // Query to fetch report data
  const [getContractJSReportData, { loading: loadingJSReport }] = useLazyQuery(
    GET_CONTRACT_JS_REPORT,
    {
      fetchPolicy: 'network-only',
      onCompleted: ({ getContractJSReport: res }) => {
        setToaster(
          res.requestSent
            ? asyncDownloadAlert
            : getDownloadAlert(
                REPORT_CONFIG[formValues.report.value].label,
                res.content.data,
                res.contentType,
                `${res.reportName}.${res.fileExtension}`,
              ),
        );
      },
      onError: () => {
        if (canViewAll && Date.now() - reportStartTime >= API_TIME_LIMIT)
          setToaster({
            type: 'warning',
            message:
              "Report generation exceeded time limit. Please try again, or select 'email the report' option.",
          });
      },
    },
  );

  const onSubmit = (fileType, lineData) => {
    setToaster(null);

    const queryParams = getJSReportQueryParams(formValues, lineData);
    let method = formValues.isEmailReport.value ? 'async' : 'sync';

    reportStartTime = Date.now();
    getContractJSReportData({
      variables: {
        input: {
          reportTypeId: REPORT_CONFIG[formValues.report.value].reportTypeId,
          outputType: fileType,
          method,
          queryParams,
        },
      },
    });
  };

  return (
    <div className="margin-bottom-8">
      {loadingJSReport && <OverlaySpinner />}
      {toaster && (
        <Alert
          slim
          focused
          type={toaster.type}
          onClose={() => setToaster(null)}
        >
          {toaster.message}
        </Alert>
      )}
      <Breadcrumbs current="Contract reports" path={breadcrumbsPath} />
      <PageTitle
        title="Contract reports"
        aria-label="Contract reports"
        tabIndex="0"
      />
      <div>
        Use this tool to export the contract reports from Fleet. All reports
        will pull the latest version of the contracts.
      </div>
      <div className="margin-bottom-4">
        Required fields are marked with an asterisk (<RequiredFieldIndicator />
        ).
      </div>

      <StepIndicator
        counterSize="small"
        data-testid="bid-reports-stepper"
        heading={STEPS[currentStep].heading}
        steps={STEPS[currentStep].steps}
        className="margin-bottom-1"
      />
      <hr className="border-gray-1" />

      {currentStep === 0 ? (
        <ReportSelectionForm
          canViewAll={canViewAll}
          formValues={formValues}
          setFormValues={setFormValues}
          onNext={() => {
            setCurrentStep(1);
            setFormValues((prevValues) => ({
              ...prevValues,
              ...STEP_2_DEFAULTS,
            }));
          }}
        />
      ) : (
        <SolicitationSelectionForm
          canViewAll={canViewAll}
          formValues={formValues}
          setFormValues={setFormValues}
          onPrev={() => setCurrentStep(0)}
          onSubmit={onSubmit}
        />
      )}
    </div>
  );
};

export default ContractReports;
